import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Body, Box, Button, CompositionHero, Container, Heading, Hero, HtmlContent } from 'designsystem';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import {
    apiImageToOpenGraph,
    ContentBlockTickets,
    fetchApiData,
    fetchCraftData,
    formatShowTitle,
    gtm,
    LoadingFilmPage,
    TicketDatePicker,
    useGetApiImageProps,
} from 'shared';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import {
    CompositionDetailQuery,
    CompositionDetailQueryVariables,
    TicketAvailabilityStatusEnum,
    useCompositionDetailQuery,
} from '../../../gql/api';
import React, { ComponentProps, Ref, useEffect, useMemo, useRef } from 'react';
import CompositionDetailPageContent from '../../../components/pages/CompositionDetailPageContent';
import { FormattedMessage, useIntl } from 'react-intl';
import useToggleCalendar from '../../../hooks/useToggleCalendar';
import { FilmAlertQuery, FilmAlertQueryVariables, useFilmAlertQuery } from '../../../gql/cms';
import { useTheme } from '@emotion/react';
import striptags from 'striptags';
import { NextSeo } from 'next-seo';
import { DEFAULT_REVALIDATION_TIME } from '../../[...uri]';
import siteHandleByLocale from '../../../utils/siteHandleByLocale';
import Link from 'next/link';

const CompositionDetailPage: NextPage = () => {
    const { query, isFallback, locale, asPath } = useRouter();
    const { data } = useCompositionDetailQuery({ id: query.id as string });
    const composition = data?.currentFestivalComposition;
    const shows = composition?.shows;
    const getImgProps = useGetApiImageProps();
    const intl = useIntl();
    const tickets: ComponentProps<typeof CompositionDetailPageContent>['tickets'] = useMemo(
        () =>
            shows?.map(show => ({
                title: formatShowTitle(show, intl),
                location: show.location,
                accessibility: show.accessibility
                    ?.map(a => a.translation)
                    .filter(Boolean)
                    .join(', '),
                startDate: new Date(show.startOn),
                endDate: new Date(show.endOn),
                ticketType: 'show',
                soldOut: show.ticketAvailabilityStatus === TicketAvailabilityStatusEnum.SoldOut,
                unavailable: show.noSale,
                id: show.id,
                externalTicketLink: show.externalSaleLink,
            })),
        [shows, intl]
    );

    const ticketBlockRef: Ref<HTMLDivElement> = useRef();
    const scrollToTicketBlock = () => ticketBlockRef?.current?.scrollIntoView({ behavior: 'smooth' });
    const { toggleCalendar, isAddedToCalendar, isLoadingCalendar } = useToggleCalendar();

    const { data: alertData } = useFilmAlertQuery({
        site: siteHandleByLocale[locale],
    });
    const alert = alertData?.globalSet?.__typename === 'festivalfilmAlert_GlobalSet' && alertData.globalSet;
    const theme = useTheme();
    const [firstTicket] = tickets ?? [];
    const showDatepicker = useMemo(
        () =>
            tickets?.every(ticket => firstTicket?.location === ticket.location && !ticket.unavailable) &&
            tickets.length > 5,
        [firstTicket?.location, tickets]
    );
    const ogImage = apiImageToOpenGraph(composition?.publications?.favoriteImage);

    useEffect(() => {
        if (composition?.fullTitle) {
            gtm.pageView(asPath, {
                page_pillar: 'festival',
                page_navigation: composition.fullTitle,
                page_language: locale,
            });
        }
    }, [asPath, composition?.fullTitle, locale]);

    if (isFallback) {
        return <LoadingFilmPage />;
    }

    return (
        composition && (
            <>
                <NextSeo
                    title={`${composition.fullTitle}`}
                    description={striptags(composition.description?.translation)}
                    openGraph={{
                        images: ogImage,
                    }}
                    titleTemplate="%s | IDFA Festival"
                />
                {composition.special ? (
                    <CompositionHero
                        title={composition.fullTitle}
                        image={getImgProps(
                            composition?.publications?.favoriteImage ?? composition?.publications?.stills?.[0],
                            composition?.fullTitle,
                            '100vw'
                        )}
                    >
                        {tickets.length > 0 && (
                            <Box flex={1} h="100%">
                                {!showDatepicker ? (
                                    <ContentBlockTickets
                                        tickets={tickets}
                                        mb={0}
                                        h="100%"
                                        toggleCalendar={toggleCalendar}
                                        isAddedToCalendar={isAddedToCalendar}
                                        isLoadingCalendar={isLoadingCalendar}
                                        tooltipContent={
                                            <Body>
                                                <FormattedMessage
                                                    defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                    id="accessibility.tooltip"
                                                    values={{
                                                        a: children => (
                                                            <Link
                                                                href={
                                                                    locale === 'nl'
                                                                        ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                        : '/en/plan-your-visit/locations-and-accessibility/'
                                                                }
                                                            >
                                                                {children}
                                                            </Link>
                                                        ),
                                                    }}
                                                />
                                            </Body>
                                        }
                                    />
                                ) : (
                                    <TicketDatePicker
                                        tickets={tickets}
                                        isAddedToCalendar={isAddedToCalendar}
                                        toggleCalendar={toggleCalendar}
                                        isLoadingCalendar={isLoadingCalendar}
                                        tooltipContent={
                                            <Body>
                                                <FormattedMessage
                                                    defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                    id="accessibility.tooltip"
                                                    values={{
                                                        a: children => (
                                                            <Link
                                                                href={
                                                                    locale === 'nl'
                                                                        ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                        : '/en/plan-your-visit/locations-and-accessibility/'
                                                                }
                                                            >
                                                                {children}
                                                            </Link>
                                                        ),
                                                    }}
                                                />
                                            </Body>
                                        }
                                        mb={0}
                                        h="100%"
                                    />
                                )}
                            </Box>
                        )}
                    </CompositionHero>
                ) : (
                    <Hero
                        image={getImgProps(
                            composition?.publications?.favoriteImage ?? composition?.publications?.stills?.[0],
                            composition?.fullTitle,
                            '100vw'
                        )}
                    >
                        <Heading variant={1} clamp={3}>
                            {composition?.fullTitle}
                        </Heading>
                        <Button bgColor="neutralwhite." onClick={scrollToTicketBlock} mt={8}>
                            {tickets.some(t => t.unavailable) ? (
                                <FormattedMessage defaultMessage="Tijden" />
                            ) : (
                                <FormattedMessage defaultMessage="Tickets & Tijden" />
                            )}
                        </Button>
                    </Hero>
                )}
                {alert && !!alert.filmAlert?.trim() && (
                    <Box bg={theme.tokens.SyntaxBackgroundPrimaryDefault}>
                        <Container p={4} sx={{ a: { color: 'inherit !important' } }} textAlign="center">
                            <Box>
                                <Body>
                                    <HtmlContent html={alert.filmAlert} />
                                </Body>
                            </Box>
                        </Container>
                    </Box>
                )}
                <CompositionDetailPageContent
                    composition={composition}
                    tickets={tickets}
                    ticketBlockRef={ticketBlockRef}
                    showDatePicker={showDatepicker}
                />
            </>
        )
    );
};

export const getStaticPaths: GetStaticPaths = async () => ({
    // we don't provide paths because we won't fetch any comps during build time.
    paths: [],

    fallback: true,
});

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    const queryClient = new QueryClient();

    const variables = {
        id: params?.id as string,
    };

    const data = await queryClient.fetchQuery(useCompositionDetailQuery.getKey(variables), () =>
        fetchApiData<CompositionDetailQuery, CompositionDetailQueryVariables>({
            locale,
            query: useCompositionDetailQuery.document,
            variables,
        })
    );

    const filmAlertVariables: FilmAlertQueryVariables = {
        site: siteHandleByLocale[locale],
    };

    await queryClient.prefetchQuery(useFilmAlertQuery.getKey(filmAlertVariables), () =>
        fetchCraftData<FilmAlertQuery, FilmAlertQueryVariables>({
            query: useFilmAlertQuery.document,
            variables: filmAlertVariables,
        })
    );

    if (!data?.currentFestivalComposition) {
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default CompositionDetailPage;
